import React, { Fragment, useEffect } from 'react';
import { History, Loader, Send } from 'app/BargainWidget/Bargains/WidgetV1/icons/Icons';
import { useBargain } from 'app/BargainWidget/context/BargainContext';
import useCreateOffer from 'app/BargainWidget/hooks/useCreateOffer';
import { useForm } from 'react-hook-form';
import QuantityFieldV1 from 'app/BargainWidget/Bargains/WidgetV1/components/forms/QuantityFieldV1';
import OfferPriceFieldV1 from 'app/BargainWidget/Bargains/WidgetV1/components/forms/OfferPriceFieldV1';
import NotesFieldV1 from 'app/BargainWidget/Bargains/WidgetV1/components/forms/NotesFieldV1';
import TermsConditionFieldV1 from 'app/BargainWidget/Bargains/WidgetV1/components/forms/TermsConditionFieldV1';
import BargainCountDownV1 from 'app/BargainWidget/Bargains/WidgetV1/components/BargainCountDownV1';
import BargainProductInfoV1 from 'app/BargainWidget/Bargains/WidgetV1/components/BargainProductInfoV1';
import { darkenHexColor } from 'app/BargainWidget/Bargains/utils/common-utils';
import { parseHtmlOrString } from 'app/BargainWidget/Bargains/utils/parse-utils';
import CustomerEmailFieldV1 from 'app/BargainWidget/Bargains/WidgetV1/components/forms/CustomerEmailFieldV1';
import CustomerNameFieldV1 from 'app/BargainWidget/Bargains/WidgetV1/components/forms/CustomerNameFieldV1';
import { getMinimumPrice } from 'app/BargainWidget/Bargains/utils/bargain-utils';
import { getCurrency } from 'app/BargainWidget/Bargains/utils/currency-utils';
import CustomerPhoneFieldV1 from 'app/BargainWidget/Bargains/WidgetV1/components/forms/CustomerPhoneFieldV1';

const defaultFormValue = {
  quantity: 1,
  note: '',
  offerPrice: 1,
  agreedOnTermsConditions: false,
  email: null,
  name: null,
};

export function BargainWidgetV1({ isAuthenticated = false, className = '' }) {
  const {
    customer,
    bargainData,
    widgetSettings,
    translation,
    hideActionItems,
    currentVariant,
    handleAuthentication,
    handleChangeModalView,
    isGuestBargainOfferAllowed,
    shopSettings
  } = useBargain();
  const { loading, isCreated, handleCreateOffer } = useCreateOffer();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      ...defaultFormValue,
      offerPrice:
        bargainData?.applyMinimumPrice && Number(bargainData?.minimumPrice) > 0
          ? parseFloat(getMinimumPrice(bargainData, currentVariant?.price))
          : 1,
    },
  });
  const values: any = watch();

  const handleHistoryClick = async () => {
    if (!isAuthenticated) return handleAuthentication();
    handleChangeModalView('history');
  };

  const onSubmit = async data => {
    if (!isAuthenticated && !isGuestBargainOfferAllowed) return handleAuthentication();
    await handleCreateOffer({
      productId: currentVariant?.productId,
      variantId: currentVariant?.variantId,
      customerId: customer?.id,
      email: data?.email || customer?.email,
      customerName: data?.name || customer?.name,
      bargainRuleId: bargainData.id,
      productHandle: currentVariant?.productHandle,
      currency: getCurrency(),
      offerPrice: data?.offerPrice,
      note: data?.note,
      quantity: data?.quantity,
      phone: data?.phone,
      // agreedOnTermsConditions: data?.agreedOnTermsConditions
    });
  };

  useEffect(() => {
    if (isCreated) reset();
  }, [isCreated]);

  return (
    <div
      className={`se-w-[350px] se-bg-white se-shadow se-rounded-lg se-overflow-hidden se-font-sans bargain-widget-v1-main-wrapper ${
        className || ''
      }`}
    >
      <div
        style={
          {
            '--header-background-color': widgetSettings?.headerBackgroundColor || '#000000',
            '--heading-text-color': widgetSettings?.contentTextColor || '#ffffff',
          } as any
        }
        className="se-bg-[var(--header-background-color)] se-text-[var(--heading-text-color)] se-px-6 se-py-3 bargain-widget-v1-main-header"
      >
        <div className="se-text-lg se-font-semibold bargain-widget-v1-main-title">{translation?.titleDetails || 'Make an Offer'}</div>
        {widgetSettings?.showCountDown && <BargainCountDownV1 />}
      </div>
      {!hideActionItems && (
        <div className="se-mt-1 se-pt-1 se-pb-3 se-px-6 se-text-white bargain-widget-v1-main-content">
          <BargainProductInfoV1
            className="se-mb-1"
            title={currentVariant?.productTitle}
            imageURL={currentVariant?.imageURL}
            price={currentVariant?.price}
            offerPrice={values?.offerPrice}
            translation={translation}
            widgetSettings={widgetSettings}
            bargainRule={bargainData}
          />
          <form onSubmit={handleSubmit(onSubmit)} className={' se-text-white bargain-widget-v1-main-form'}>
            <div className="se-space-y-2 se-text-white bargain-widget-v1-main-form-spacing">
              {isGuestBargainOfferAllowed && (
                <Fragment>
                  <CustomerEmailFieldV1
                    label={translation?.email || 'Email'}
                    placeholder={translation?.placeholderEmail || 'johndoe@gmail.com'}
                    control={control}
                    widgetSettings={widgetSettings}
                  />
                  {!widgetSettings?.hideName && (
                    <CustomerNameFieldV1
                      label={translation?.name || 'Name'}
                      placeholder={translation?.placeholderName || 'John Doe'}
                      control={control}
                      widgetSettings={widgetSettings}
                    />
                  )}
                </Fragment>
              )}

              {!widgetSettings?.hidePhone && (
                <CustomerPhoneFieldV1
                  label={translation?.phoneNumber || 'Phone'}
                  placeholder={translation?.phoneNumberPlaceholder || '000-000-0000'}
                  control={control}
                  widgetSettings={widgetSettings}
                />
              )}

              {!widgetSettings?.hideQuantity && (
                <QuantityFieldV1
                  widgetSettings={widgetSettings}
                  control={control}
                  label={translation?.quantity}
                  placeholder={translation?.placeholderQuantity}
                />
              )}
              <OfferPriceFieldV1
                widgetSettings={widgetSettings}
                label={translation?.offerPrice}
                placeholder={translation?.placeholderPrice}
                control={control}
                bargainData={bargainData}
                translation={translation}
                currentVariant={currentVariant}
              />
              {!widgetSettings?.hideNote && (
                <NotesFieldV1
                  widgetSettings={widgetSettings}
                  control={control}
                  label={translation?.notes}
                  placeholder={translation?.placeholderNotes}
                />
              )}
              {!widgetSettings?.hideTermsAndConditions && (
                <TermsConditionFieldV1
                  widgetSettings={widgetSettings}
                  control={control}
                  label={parseHtmlOrString(translation?.termsConditionsLabel || 'I agree to the terms and conditions.')}
                />
              )}
              <div className="se-flex se-gap-2.5 se-pt-2 bargain-widget-v1-form-actions">
                {isAuthenticated && customer?.email && !widgetSettings?.hideHistoryButton && (
                  <button
                    type={'button'}
                    onClick={handleHistoryClick}
                    disabled={loading}
                    style={
                      {
                        '--history-button-background-color': widgetSettings?.historyButtonBackgroundColor || '#f3f4f6',
                        '--history-button-hover-background-color': darkenHexColor(
                          widgetSettings?.historyButtonBackgroundColor || '#f3f4f6',
                          3,
                        ),
                        '--history-button-text-color': widgetSettings?.historyButtonTextColor || '#374151',
                      } as any
                    }
                    className="se-flex se-items-center se-justify-center se-gap-1.5 se-px-4 se-py-2 se-rounded-md se-bg-[var(--history-button-background-color)] hover:se-bg-[var(--history-button-hover-background-color)] se-text-[var(--history-button-text-color)] se-flex-1 se-text-sm se-font-medium bargain-widget-v1-form-history-button"
                  >
                    <History className="se-w-3.5 se-h-3.5 bargain-widget-v1-form-history-button-icon" />
                    {translation?.viewAllOffer || 'View All Offers'}
                  </button>
                )}
                <button
                  type={'submit'}
                  disabled={loading}
                  style={
                    {
                      '--offer-button-background-color': widgetSettings?.offerButtonBackgroundColor || '#000000',
                      '--offer-button-hover-background-color': darkenHexColor(widgetSettings?.offerButtonBackgroundColor || '#000000', 3),
                      '--offer-button-text-color': widgetSettings?.offerButtonTextColor || '#ffffff',
                    } as any
                  }
                  className="se-flex se-items-center se-justify-center se-gap-1.5 se-px-4 se-py-2 se-rounded-md se-bg-[var(--offer-button-background-color)] hover:se-bg-[var(--offer-button-hover-background-color)] se-text-[var(--offer-button-text-color)] hover:se-opacity-90 disabled:se-opacity-50 disabled:hover:se-opacity-50 disabled:se-cursor-not-allowed se-flex-1 se-text-sm se-font-medium se-transition-all bargain-widget-v1-form-place-offer-button"
                >
                  {loading ? (
                    <Loader className="se-w-3.5 se-h-3.5 se-animate-spin bargain-widget-v1-form-place-offer-button-loading-icon" />
                  ) : (
                    <Send className="se-w-3.5 se-h-3.5 bargain-widget-v1-form-place-offer-button-icon" />
                  )}
                  {loading ? '' : translation?.proposeAnOffer || 'Send Offer'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      {
        shopSettings?.showBranding !== false && (
          <div
            ref={node => {
              node?.style?.setProperty('display', 'block', 'important');
              node?.style?.setProperty('visibility', 'visible', 'important');
            }}
            className="se-bg-inherit se-text-[#757575] se-w-full se-pb-3 se-pt-0 se-z-[3000]"
            style={{ boxShadow: '0 -3px 12px 0 rgba(0, 0, 0, 0.04)' }}
          >
            <div
              ref={node => {
                node?.style?.setProperty('display', 'block', 'important');
                node?.style?.setProperty('visibility', 'visible', 'important');
              }}
              onClick={() => window.open('https://apps.shopify.com/bargain-make-an-offer', '_blank')}
              className="se-underline se-text-center se-font-normal se-text-xs se-text-inherit se-cursor-pointer"
            >
              Powered by Bargain - Make an Offer
            </div>
          </div>
        )
      }
    </div>
  );
}
